import { createRoot, hydrateRoot } from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import { GlobalErrorBoundary } from './components/GlobalErrorBoundary'
import { 
  initResourceMonitor, 
  setupNetworkMonitoring, 
  addResourceMonitorToWindow 
} from './utils/externalResourceMonitor'

// Detect iframe and set preview flag
const isInIframe = window.self !== window.top;
(window as any).__IN_PREVIEW__ = isInIframe;
if (isInIframe) {
  console.log('🖼️ Running in iframe - applying compatibility mode');
}

// Cached analytics module loader
let analyticsModule: Promise<typeof import('@/utils/analyticsService')> | null = null;
const loadAnalytics = () => {
  return analyticsModule || (analyticsModule = import('@/utils/analyticsService'));
};

console.log("Application starting up");

// Initialize resource monitoring (only in development)
if (process.env.NODE_ENV !== 'production') {
  initResourceMonitor();
  setupNetworkMonitoring();
  addResourceMonitorToWindow();
  console.log("WordPress resource monitoring enabled in development mode");
}


// Initialize Google Analytics after DOM is ready
document.addEventListener('DOMContentLoaded', async () => {
  console.log('Initializing GA4 tracking...');
  const { initializeGA, testGA4Tracking } = await loadAnalytics();
  initializeGA();
  
  // Test GA4 in development mode only
  if (process.env.NODE_ENV !== 'production') {
    setTimeout(testGA4Tracking, 3000);
  }
});

// Improved route change handler with debouncing
let routeChangeTimeout: number | null = null;
const routeChangeListener = () => {
  // Clear any pending timeouts to prevent multiple rapid calls
  if (routeChangeTimeout) {
    window.clearTimeout(routeChangeTimeout);
  }
  
  // Debounce the route change handling
  routeChangeTimeout = window.setTimeout(async () => {
    const currentPath = window.location.pathname;
    console.log("Route changed to:", currentPath);
    
    // Track page view in analytics
    const { trackPageView, trackSEOMetrics } = await loadAnalytics();
    trackPageView(currentPath);
    
    // Track SEO metrics with throttling - reduced frequency
    setTimeout(trackSEOMetrics, 1000);
    
    // Extra logging for blog paths
    if (currentPath.includes('/blog/')) {
      console.log("Blog post ID detected:", currentPath.split('/blog/')[1]);
    }

    // Debug for auction links
    if (currentPath.includes('/veiling')) {
      console.log("VEILING PAGE DETECTED:", currentPath);
    }
  }, 150);
};

// Add history listeners
window.addEventListener('popstate', routeChangeListener);
window.addEventListener('hashchange', routeChangeListener);

// Custom navigation monitoring with optimized method overrides
const originalPushState = history.pushState;
history.pushState = function() {
  originalPushState.apply(this, arguments as any);
  routeChangeListener();
};

const originalReplaceState = history.replaceState;
history.replaceState = function() {
  originalReplaceState.apply(this, arguments as any);
  routeChangeListener();
};

// Log click events on auction links - optimized with event delegation
document.addEventListener('click', (e) => {
  const target = e.target as HTMLElement;
  const linkElement = target.closest('a');
  if (linkElement && linkElement.href) {
    if (linkElement.href.includes('veiling')) {
      console.log('Auction link clicked:', linkElement.href);
    }
    
    // Track external links for backlink analysis
    if (linkElement.hostname !== window.location.hostname && window.gtag) {
      const linkText = linkElement.textContent || linkElement.getAttribute('aria-label') || '';
      window.gtag('event', 'click', {
        'event_category': 'outbound',
        'event_label': linkElement.href,
        'transport_type': 'beacon'
      });
      console.log('External link tracked:', linkElement.href);
    }
  }
});

// Initial render
const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element not found");
}

const appTree = (
  <GlobalErrorBoundary>
    <App />
  </GlobalErrorBoundary>
);

// Prerendered routes ship real markup in #root — hydrate it instead of
// discarding it with a fresh client-side render.
if (rootElement.hasChildNodes()) {
  hydrateRoot(rootElement, appTree);
} else {
  createRoot(rootElement).render(appTree);
}

// Track initial page load - always track, consent handled internally
loadAnalytics().then(({ trackPageView }) => {
  trackPageView(window.location.pathname);
});

// Track SEO metrics after initial page load
window.addEventListener('load', async () => {
  const { trackSEOMetrics } = await loadAnalytics();
  setTimeout(() => {
    trackSEOMetrics();
  }, 1000);
});
console.log("Initial route:", window.location.pathname);
console.log("GA4 tracking should be active");
